import { useParams, Navigate, Link } from "react-router-dom";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { ContentShell } from "@/components/ContentShell";
import { Seo } from "@/components/Seo";
import { ARTICLES } from "@/data/insightsContent";

export default function InsightArticle() {
  const { slug } = useParams();
  const article = ARTICLES[slug];
  if (!article) return <Navigate to="/insights" replace />;

  const seo = (
    <Seo title={`${article.title} | RetainProof`} description={article.description} path={`/insights/${slug}`} />
  );

  return (
    <ContentShell seo={seo} ctaTitle="See what an independent review surfaces for your firm.">
      <article className="max-w-3xl mx-auto px-6 pt-36 pb-16" data-testid="insight-article">
        <Link to="/insights" className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-white mb-8" data-testid="insight-back-link">
          <ArrowLeft className="h-3.5 w-3.5" /> All insights
        </Link>
        <p className="font-mono-data text-xs uppercase tracking-[0.25em] text-[#D5A44A] mb-4">{article.read}</p>
        <h1 className="font-display font-light text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight" data-testid="insight-title">
          {article.title}
        </h1>

        <div className="mt-10 space-y-8">
          {article.blocks.map((b, i) => (
            <div key={i}>
              {b.h && <h2 className="font-display text-2xl font-light tracking-tight text-white mb-3">{b.h}</h2>}
              {b.p && b.p.map((para, j) => (
                <p key={j} className="text-zinc-300 leading-relaxed mb-3" dangerouslySetInnerHTML={{ __html: para.replace(/</g, "&lt;") }} />
              ))}
              {b.list && (
                <ul className="mt-2 space-y-2.5">
                  {b.list.map((li) => (
                    <li key={li} className="flex gap-3 text-zinc-300 leading-relaxed"><span className="text-[#D5A44A] mt-0.5">→</span>{li}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>

        {article.note && (
          <p className="mt-10 text-xs text-zinc-500 leading-relaxed border-t border-white/10 pt-6">{article.note}</p>
        )}

        {article.related && (
          <div className="mt-10 border-t border-white/10 pt-8">
            <p className="font-mono-data text-[10px] uppercase tracking-widest text-zinc-500 mb-4">Keep reading</p>
            <div className="flex flex-wrap gap-4 text-sm">
              {article.related.map((href) => (
                <Link key={href} to={href} className="inline-flex items-center gap-1.5 text-[#D5A44A] hover:text-[#e6bd6f] group" data-testid={`insight-related-${href}`}>
                  {LABELS[href] || href} <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                </Link>
              ))}
            </div>
          </div>
        )}
      </article>
    </ContentShell>
  );
}

const LABELS = {
  "/law-firm-marketing-audit": "Law firm marketing audit",
  "/cost-per-signed-case": "Cost per signed case",
};
